import React from "react";
import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import type { FormData, GeneratedContent } from "../types";
import { formatDate } from "../utils";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
  },
  header: {
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 2,
    borderBottomColor: "#1e40af",
  },
  schoolName: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#1e3a8a",
    textAlign: "center",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  professorName: {
    fontSize: 12,
    color: "#4b5563",
    textAlign: "center",
    marginBottom: 8,
  },
  infoGrid: {
    flexDirection: "row",
    backgroundColor: "#f3f4f6",
    padding: 10,
    borderWidth: 1,
    borderColor: "#1e40af",
    borderRadius: 4,
    marginBottom: 10,
  },
  infoItem: {
    flex: 1,
    fontSize: 10,
    color: "#1e3a8a",
  },
  label: {
    fontWeight: "bold",
  },
  studentInfo: {
    flexDirection: "row",
    marginTop: 10,
    justifyContent: "space-between",
  },
  studentField: {
    fontSize: 11,
    color: "#111827",
    borderBottomWidth: 1,
    borderBottomColor: "#9ca3af",
    flex: 2,
    marginRight: 20,
    paddingBottom: 2,
  },
  gradeField: {
    fontSize: 11,
    color: "#111827",
    borderBottomWidth: 1,
    borderBottomColor: "#9ca3af",
    flex: 0.5,
    paddingBottom: 2,
  },
  titleSection: {
    textAlign: "center",
    marginVertical: 20,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1e3a8a",
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 11,
    color: "#6b7280",
  },
  instructions: {
    backgroundColor: "#eff6ff",
    borderLeftWidth: 4,
    borderLeftColor: "#1e40af",
    padding: 10,
    marginVertical: 15,
    borderRadius: 2,
  },
  instructionText: {
    fontSize: 10,
    color: "#1e40af",
    marginBottom: 2,
  },
  section: {
    marginTop: 15,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    color: "#1e3a8a",
    borderBottomWidth: 2,
    borderBottomColor: "#1e40af",
    paddingBottom: 4,
    marginBottom: 10,
    textTransform: "uppercase",
  },
  question: {
    marginBottom: 15,
  },
  questionHeader: {
    flexDirection: "row",
    marginBottom: 6,
  },
  questionNumber: {
    backgroundColor: "#eff6ff",
    color: "#1e3a8a",
    padding: "2 6",
    borderRadius: 3,
    fontWeight: "bold",
    fontSize: 10,
    marginRight: 8,
  },
  questionText: {
    fontSize: 11,
    color: "#1f2937",
    flex: 1,
    lineHeight: 1.4,
  },
  alternatives: {
    marginLeft: 25,
    marginTop: 5,
  },
  alternative: {
    fontSize: 10,
    color: "#374151",
    marginBottom: 4,
    flexDirection: "row",
  },
  checkbox: {
    width: 10,
    height: 10,
    borderWidth: 1,
    borderColor: "#9ca3af",
    marginRight: 6,
    marginTop: 2,
  },
  answerBox: {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#9ca3af",
    padding: 20,
    marginTop: 8,
    minHeight: 80,
    backgroundColor: "#f9fafb",
  },
  answerPlaceholder: {
    fontSize: 9,
    color: "#9ca3af",
    textAlign: "center",
  },
  valueBadge: {
    color: "#1e40af",
    fontWeight: "bold",
    fontSize: 10,
  },
  footer: {
    marginTop: 30,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    textAlign: "center",
  },
  footerText: {
    fontSize: 9,
    color: "#9ca3af",
    marginBottom: 2,
  },
});

interface EvaluationPDFProps {
  formData: FormData;
  content: GeneratedContent;
}

export const EvaluationPDF: React.FC<EvaluationPDFProps> = ({
  formData,
  content,
}) => {
  return (
    <Document title={formData.title}>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.schoolName}>
            {formData.schoolName || "INSTITUIÇÃO DE ENSINO"}
          </Text>
          <Text style={styles.professorName}>
            Professor(a): {formData.professorName || "Não informado"}
          </Text>

          <View style={styles.infoGrid}>
            <View style={styles.infoItem}>
              <Text>
                <Text style={styles.label}>Disciplina: </Text>
                {formData.subject}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text>
                <Text style={styles.label}>Turma: </Text>
                {formData.class}
              </Text>
            </View>
            <View style={styles.infoItem}>
              <Text>
                <Text style={styles.label}>Data: </Text>
                {formatDate(formData.dueDate)}
              </Text>
            </View>
          </View>

          <View style={styles.studentInfo}>
            <View style={styles.studentField}>
              <Text>
                <Text style={styles.label}>Aluno(a): </Text>
                _______________________________________
              </Text>
            </View>
            <View style={styles.gradeField}>
              <Text>
                <Text style={styles.label}>Nota: </Text>
                ___________
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.titleSection}>
          <Text style={styles.title}>{formData.title}</Text>
          <Text style={styles.subtitle}>
            Valor Total: {formData.weight} pontos
          </Text>
        </View>

        {content.orientacoes_professor && (
          <View style={styles.instructions}>
            <Text
              style={[
                styles.instructionText,
                { fontWeight: "bold", marginBottom: 4 },
              ]}
            >
              Instruções:
            </Text>
            {content.orientacoes_professor.slice(0, 4).map((inst, i) => (
              <Text key={i} style={styles.instructionText}>
                • {inst}
              </Text>
            ))}
          </View>
        )}

        {content.estrutura_avaliacao?.questoes_objetivas && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Parte I - Questões Objetivas (
              {content.estrutura_avaliacao.questoes_objetivas.valor_cada} pontos
              cada)
            </Text>
            {content.estrutura_avaliacao.questoes_objetivas.exemplos.map(
              (q, i) => (
                // Prevent questions from breaking across pages in the PDF layout
                <View key={i} style={styles.question} wrap={false}>
                  <View style={styles.questionHeader}>
                    <Text style={styles.questionNumber}>{q.numero}</Text>
                    <Text style={styles.questionText}>{q.enunciado}</Text>
                  </View>
                  <View style={styles.alternatives}>
                    {q.alternativas.map((alt, j) => (
                      <View key={j} style={styles.alternative}>
                        <View style={styles.checkbox} />
                        <Text style={{ flex: 1 }}>{alt}</Text>
                      </View>
                    ))}
                  </View>
                </View>
              ),
            )}
          </View>
        )}

        {content.estrutura_avaliacao?.questoes_dissertativas && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Parte II - Questões Dissertativas
            </Text>
            {content.estrutura_avaliacao.questoes_dissertativas.exemplos.map(
              (q, i) => (
                <View key={i} style={styles.question} wrap={false}>
                  <View style={styles.questionHeader}>
                    <Text style={styles.questionNumber}>{q.numero}</Text>
                    <View style={{ flex: 1 }}>
                      <Text style={styles.questionText}>
                        {q.enunciado}{" "}
                        <Text style={styles.valueBadge}>
                          ({q.valor} pontos)
                        </Text>
                      </Text>
                    </View>
                  </View>
                  <View style={styles.answerBox}>
                    <Text style={styles.answerPlaceholder}>
                      Espaço reservado para a resposta do aluno
                    </Text>
                  </View>
                </View>
              ),
            )}
          </View>
        )}

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>
            Bom trabalho! Mantenha o foco e a calma. 📚
          </Text>
          <Text style={styles.footerText}>
            Gerado por SIGA - Sistema Inteligente de Gestão Acadêmica
          </Text>
          {content.habilidades_bncc && (
            <Text style={[styles.footerText, { marginTop: 4 }]}>
              Habilidades BNCC:{" "}
              {content.habilidades_bncc.map((h: any) => h.codigo).join(", ")}
            </Text>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default EvaluationPDF;
